import { useInfiniteQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { AllURLParams, HotelDetailURLParams } from "@/lib/Types/URLParameters";
import { dateListResponse } from "@/lib/Types/DateListResponse/DateListResponse";
import fetchData from "@/lib/Fetchers/fetchData";
import { sendKibanaLog } from "@/app/api/sendKibanaLog";
import { addMinutes, format, startOfDay } from "date-fns";
import { useReactiveSearchParams } from "@/lib/Hooks/useReactiveSearchParams";
import { getFilterKey } from "@/lib/Helpers/getFilterKey";
import { usePathname } from "next/navigation";
import { viewModeFromPathAndQuery } from "@/lib/Helpers/currentUrl";
import { getOseTrackingId } from "@/lib/Helpers/manageOseTrackingId";
import { uidGenerator } from "@/lib/Helpers/uidGenerator";
import { AppConfig } from "@/lib/Types/AppConfig";
import { getIsLoggedIn } from "@/lib/Helpers/userHelpers";
import { isTransientApiError } from "@/lib/Types/ApiError";
import {
  EmptyResultsError,
  NetworkFetchError,
  NonRetryableError,
  searchQueryRetryConfig,
} from "@/lib/Types/searchErrors";

export const PAGE_SIZE = 5;

function convertMinutesToTime(minutes: string | undefined): string | undefined {
  if (!minutes) return undefined;
  const totalMinutes = parseInt(minutes, 10);
  if (isNaN(totalMinutes)) return undefined;
  const date = addMinutes(startOfDay(new Date()), totalMinutes);
  return format(date, "HH:mm");
}

interface DateListSearchParams {
  app: boolean;
  cid: string;
  channel: string;
  device: string;
  from: string;
  to?: string;
  rooms: string;
  sort?: string;
  p: number;
  m: number;
  userUid: string;
  isLoggedIn: boolean;
  multiroom: boolean;
  page: string;
  searchId?: string;
  subsource?: string;
  departureAirport?: string[];
  utmCampaign?: string;
  duration?: string;
  dateExact?: boolean;
  specialDealTypes?: string;
  platformName: string;
  directFlight?: boolean;
  mealType?: string;
  roomType?: string;
  price?: string;
  roomAddition?: string;
  departureFlightTimeFrom?: string;
  departureFlightTimeUntil?: string;
  returnFlightTimeFrom?: string;
  returnFlightTimeUntil?: string;
  categories?: string[];
  transfer?: boolean;
  railAndFly?: boolean;
  flexibleOffersExpected?: boolean;
  tourOperator?: string;
  rentalCar?: boolean;
  flexibleOffers?: string;
  bookOnOption?: string;
  airlineCodes?: string;
  oseTrackingId?: string;
}

async function fetchDateListSearch(
  searchParams: HotelDetailURLParams,
  config: AppConfig,
): Promise<dateListResponse> {
  const { envConfig, pathname, viewMode, userData, mpt, WlConfig } = config;
  const hotelCID = pathname?.match(/cid_(\d+)/) ?? [];
  if (!hotelCID[1]) {
    throw new NonRetryableError("dateListSearch: missing hotel CID");
  }
  const isDurationZero = searchParams.duration === "0";

  // Match Sunrise (wegde-ibe/src/selectors/dateList/dateList.ts): branch on
  // `backUrl` — the OPX entry signal. With backUrl, reuse the persisted tracking
  // id (written by providers.tsx on URL-supplied `trackingId`); otherwise always
  // generate fresh. Relying on localStorage without an OPX context causes stale
  // IDs from previous sessions → no results from backend.
  const oseTrackingId = searchParams?.backUrl
    ? (getOseTrackingId() ?? searchParams.trackingId)
    : `${WlConfig.oseTrackingIdBrand}_${uidGenerator().generate(searchParams.searchId ?? "")}`;

  const specialDealTypes = searchParams.toVoucher
    ? "TO_VOUCHER"
    : searchParams.specialDeals
      ? "DISCOUNT_PRICE,TO_VOUCHER"
      : undefined;

  const paramsObject: DateListSearchParams = {
    app: searchParams.app === "true",
    channel: viewMode.channel,
    cid: hotelCID[1],
    device: userData?.deviceType ?? "",
    from: searchParams.priceCurveFrom || searchParams.from,
    // When priceCurveFrom is set, omit `to` — backend uses from + duration.
    ...(!searchParams.priceCurveFrom && searchParams.to && { to: searchParams.to }),
    rooms: searchParams.rooms,
    p: viewMode.profile,
    m: viewMode.mandator,
    userUid: userData?.userUid || "",
    isLoggedIn: getIsLoggedIn(),
    multiroom: searchParams.rooms.includes(";"),
    page: searchParams.page || "1_5",
    subsource: searchParams.bf_subsource || "S17PDV10S17PD01",
    platformName: "ICARUS",

    ...(oseTrackingId && { oseTrackingId }),
    ...(searchParams.sort && { sort: searchParams.sort }),
    ...(searchParams.mealType && { mealType: searchParams.mealType }),
    ...(searchParams.roomType && { roomType: searchParams.roomType }),
    ...(searchParams.searchId && { searchId: searchParams.searchId }),
    ...(searchParams.tourOperator && {
      tourOperator: searchParams.tourOperator,
    }),
    ...(searchParams.airline && { airlineCodes: searchParams.airline }),
    ...(searchParams.utm_campaign && {
      utmCampaign: searchParams.utm_campaign,
    }),
    ...(searchParams.directFlight === "1" && { directFlight: true }),
    ...(searchParams.transfer === "1" && { transfer: true }),
    ...(searchParams.railAndFly === "1" && { railAndFly: true }),
    ...(searchParams.rentalCar === "1" && { rentalCar: true }),
    ...(searchParams.flexibleOffersExpected === "true" && {
      flexibleOffersExpected: true,
    }),
    ...(searchParams.oceanView === "true" && { roomAddition: "OCEANVIEW" }),
    ...(searchParams.flexibleOffers && {
      flexibleOffers: "flexibleCancellation",
    }),
    ...(searchParams.bookOnOption && { bookOnOption: "true" }),

    ...(searchParams.price && {
      price: `0_${parseInt(searchParams.price).toFixed(0)}`,
    }),
    ...(searchParams.departureAirport && {
      departureAirport: searchParams.departureAirport.split(","),
    }),
    ...(searchParams.categories && {
      categories: searchParams.categories.split(","),
    }),
    ...(specialDealTypes && { specialDealTypes }),

    ...(searchParams.departureFlightTimeFrom && {
      departureFlightTimeFrom: convertMinutesToTime(
        searchParams.departureFlightTimeFrom,
      ),
    }),
    ...(searchParams.departureFlightTimeUntil &&
      searchParams.departureFlightTimeUntil !== "1440" && {
        departureFlightTimeUntil: convertMinutesToTime(
          searchParams.departureFlightTimeUntil,
        ),
      }),
    ...(searchParams.returnFlightTimeFrom && {
      returnFlightTimeFrom: convertMinutesToTime(
        searchParams.returnFlightTimeFrom,
      ),
    }),
    ...(searchParams.returnFlightTimeUntil &&
      searchParams.returnFlightTimeUntil !== "1440" && {
        returnFlightTimeUntil: convertMinutesToTime(
          searchParams.returnFlightTimeUntil,
        ),
      }),
    ...(searchParams.skyscanner_redirectid && {
      skyscannerRedirectId: searchParams.skyscanner_redirectid,
    }),
  };

  if (isDurationZero) {
    paramsObject.dateExact = true;
  } else if (searchParams.duration) {
    paramsObject.duration = searchParams.duration;
  }

  const url = `${envConfig.api}/comvel-datelistsearch-service/rest/datelistsearch`;

  sendKibanaLog({
    envConfig,
    transactionId: searchParams.transactionId,
    message: "DATE LIST START",
    component: "datelist",
    jsonMessage: paramsObject,
    searchId: searchParams.searchId,
    category: "debug",
  });

  const data = await fetchData({
    url,
    method: "GET",
    params: paramsObject,
    mpt: mpt || undefined,
    envConfig,
    service: "datelist",
    cache: "no-store",
    transactionId: searchParams.transactionId,
    searchId: searchParams.searchId,
  });

  const isFail = data?.apiError;
  const noOffers = !data?.dateList?.length;

  sendKibanaLog({
    envConfig,
    transactionId: searchParams.transactionId,
    message: `DATE LIST ${isFail ? "FAIL" : noOffers ? "SUCCESS_EMPTY" : "SUCCESS"}`,
    component: "datelist",
    jsonMessage: isFail && data.message,
    searchId: searchParams.searchId,
    category: "debug",
  });

  // Throw on fetch errors or empty first page so React Query retries before
  // showing empty state to the user. Network/abort/5xx errors get more retries
  // (see retry callback) than empty backend results — the backend gave a
  // definitive answer for the latter.
  if (isFail) {
    sendKibanaLog({
      envConfig,
      transactionId: searchParams.transactionId,
      message: "DATE LIST RETRY",
      component: "datelist",
      searchId: searchParams.searchId,
      category: "debug",
      jsonMessage: {
        reason: data.message,
        page: searchParams.page,
        urlParams: searchParams,
      },
    });
    if (isTransientApiError(data)) {
      throw new NetworkFetchError(data.message);
    }
    throw new EmptyResultsError(data.message || "dateListSearch: fetch failed");
  }
  if (noOffers && searchParams.page === `1_${PAGE_SIZE}`) {
    sendKibanaLog({
      envConfig,
      transactionId: searchParams.transactionId,
      message: "DATE LIST RETRY",
      component: "datelist",
      searchId: searchParams.searchId,
      category: "debug",
      jsonMessage: {
        reason: "empty_date_list",
        page: searchParams.page,
        urlParams: searchParams,
      },
    });
    throw new EmptyResultsError("empty_date_list");
  }

  return data;
}

export function useDateListSearch(appConfig: AppConfig) {
  const { envConfig, WlConfig } = appConfig;
  const searchParams = useReactiveSearchParams();
  const pathname = usePathname();

  // Convert URLSearchParams to object for fetch and key generation
  const searchParamsObject = useMemo(() => {
    const params: Record<string, string> = {};
    searchParams.forEach((value, key) => {
      params[key] = value;
    });
    return params as HotelDetailURLParams;
  }, [searchParams]);

  // Derive viewMode directly from URL to avoid race conditions with store updates
  const viewMode = viewModeFromPathAndQuery(
    pathname,
    searchParamsObject as AllURLParams,
    WlConfig.viewMode,
  );

  // Exclude searchId from cache key (it's a result, not a filter)
  const filterKey = useMemo(
    () =>
      getFilterKey(searchParamsObject, [
        "searchId",
        "preselectedOfferId",
        "clickId",
      ]),
    [searchParamsObject],
  );

  const query = useInfiniteQuery({
    queryKey: ["dateList", viewMode?.channel, filterKey],
    queryFn: ({ pageParam }) => {
      const params = {
        ...searchParamsObject,
        page: pageParam,
      } as HotelDetailURLParams;
      return fetchDateListSearch(params, appConfig);
    },
    initialPageParam: `1_${PAGE_SIZE}`,
    getNextPageParam: (lastPage, allPages) => {
      // Calculate total items fetched so far
      const totalFetched = allPages.reduce(
        (sum, page) => sum + (page.dateList?.length ?? 0),
        0,
      );
      // If the last page returned fewer items than expected, there are no more
      if ((lastPage.dateList?.length ?? 0) < PAGE_SIZE) {
        return undefined;
      }
      // Next page starts after current total
      return `${totalFetched + 1}_${totalFetched + PAGE_SIZE}`;
    },
    // Disable caching to ensure we always fetch fresh data when filters change.
    // This is important because the backend caches QuickVacancy results,
    // and we need to get the checked offer state after filter toggle.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    ...searchQueryRetryConfig,
    enabled: !!envConfig && !!viewMode,
  });

  // After retries are exhausted, log network failures and empty-result errors
  // separately so dashboards can track the user-visible empty state cleanly.
  useEffect(() => {
    if (!query.error) return;
    const isNetwork = query.error instanceof NetworkFetchError;
    sendKibanaLog({
      envConfig,
      transactionId: searchParamsObject?.transactionId,
      message: isNetwork
        ? "DATE LIST NETWORK FAILURE"
        : "DATE LIST NO RESULTS SHOWN",
      component: "datelist",
      searchId: searchParamsObject?.searchId,
      category: "error",
      jsonMessage: {
        reason: query.error.message,
        urlParams: searchParamsObject,
      },
    });
  }, [query.error]); // eslint-disable-line react-hooks/exhaustive-deps

  // Flatten pages into a single dateListResponse-like object
  const flattenedData = useMemo(() => {
    if (!query.data?.pages?.length) return undefined;

    const firstPage = query.data.pages[0];
    const allOffers = query.data.pages.flatMap((page) => page.dateList ?? []);

    return {
      ...firstPage,
      dateList: allOffers,
    };
  }, [query.data]);

  // return updated search params only when new data is fetched
  // this avoids firing tracking and other side effect before new data
  // has started fetching
  const stableSearchParams = useMemo(() => {
    return searchParams;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.dataUpdatedAt]);

  return {
    data: flattenedData,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    error: query.error,
    searchParams: stableSearchParams,
  };
}
